import { isEmpty, isNil, uniq } from 'lodash';
import moment from 'moment-timezone';

import { displayTime } from '../../../helper/time/display-time/display-time';
import { formatDuration } from '../../../helper/time/format-duration/format-duration';
import { AsyncServiceRequest } from '../../async-service-request/generic/async-service-request/async-service-request';
import { ServiceLimit } from '../../communication/service-limit/service-limit/service-limit';
import { CommunicationDisplayAlertType } from '../../core/communication/communication-display-alert-type';
import { DisplayableCommunication } from '../../core/communication/displayable-communication';
import { CommunicationType } from '../../core/communication/types';
import { isNilOrDefault } from '../../generic/serialization/is-nil-or-default';
import { getUserDisplayNameByUserId } from '../../user/user-data/helper/get-user-display-name-by-user-id/get-user-display-name-by-user-id';
import { CallEntrypointSchema } from '../../voice-response-command/call-entrypoint/call-entrypoint-schema';
import { SupportedLanguages } from '../../voice-response-command/vrc-audio-metadata/supported-languages';
import { CallList } from '../call-list/call-list/call-list';
import { CallListSchema } from '../call-list/call-list/call-list-schema';
import { IVR } from '../ivr/ivr/ivr';

import { CallLog } from './call-log';
import { CallLogDisplayConstructor } from './call-log-display-constructor';
import { CallState } from './call-state';
import { DisconnectReason } from './disconnect-reason';
import { DisconnectedBy } from './disconnected-by';
import { StoppedDialingReason } from './stopped-dialing-reason';

const SUSPECTED_ABANDONED_FORWARDED_CALL_THRESHOLD_MS = 2 * 60 * 1000;

const callListSchema = new CallListSchema();
const callEntrypointSchema = new CallEntrypointSchema();

export const CHANGE_WHO_IS_ON_CALL_HELP_URL =
  'https://helplinesoftware.zendesk.com/hc/en-us/articles/17770585624859-How-do-I-change-who-is-on-call-for-a-particular-call-type';

export const CHANGE_ADVOCATES_ASSIGNED_TO_SHIFT_HELP_URL =
  'https://helplinesoftware.zendesk.com/hc/en-us/articles/4409474782363-How-Can-I-Change-The-Advocates-Assigned-to-A-Shift';

/**
 * This class is used by call-table.component.ts. It transforms CallData(), which simply
 * holds all the properties of a call, to a format suitable for displaying in a table.
 */
export class CallLogDisplay implements DisplayableCommunication {
  ////////////////////////////////////////////////////
  // Variables
  ////////////////////////////////////////////////////

  public callList: CallList | undefined;

  public callListName: string;

  public callEntrypointName: string;

  public callLog: CallLog;

  private asyncServiceRequest: AsyncServiceRequest | undefined;

  private serviceLimitMap: Map<string, ServiceLimit>;

  ////////////////////////////////////////////////////
  // Displayable Communication Interface
  ////////////////////////////////////////////////////

  communicationType = CommunicationType.incomingPhoneCall;

  communicationTypeDetail: string;

  id: string;

  day: string;

  receiveTime: string;

  holdDuration: string;

  talkDuration: string;

  answeredBy: string;

  end: string;

  alertType: CommunicationDisplayAlertType;

  timestamp: Date;

  ////////////////////////////////////////////////////
  // Specialized Vars
  ////////////////////////////////////////////////////

  receivedAtE164Phone: string;

  disconnectReason: string;

  stoppedDialingReason: string;

  summary: string;

  numCounselorsOnCall: string;

  hasAnonymousCaller: string;

  language: string;

  incomingCallSID: string;

  hasCallbackRequest: string;

  forwardedTo?: string;

  menuOption?: string;

  /**
   * Initialize all properties based on the Call Data and UserDataMap
   * @param callLog Data associated to the call
   * @param allUserDataMap Map specifying UserData for each userId string.
   */
  constructor({
    allUserDataMap,
    asyncServiceRequest,
    callEntrypointMap,
    callListMap,
    callLog,
    ivrsMap,
    serviceLimitMap,
    timezone,
  }: CallLogDisplayConstructor) {
    this.callLog = callLog;
    this.callList = callListMap.get(this.callLog.getCallListId());
    const callEntrypoint = callEntrypointMap.get(this.callLog.getCallEntrypointId() ?? '');

    const callListName = this.callList?.getDisplayName() ?? '';
    this.callListName = isNilOrDefault(callListName, CallListSchema.displayName, callListSchema) ? '' : callListName;

    const callEntrypointName = callEntrypoint?.getDisplayName() ?? '';
    this.callEntrypointName = isNilOrDefault(callEntrypointName, CallEntrypointSchema.displayName, callEntrypointSchema)
      ? ''
      : callEntrypointName;
    this.asyncServiceRequest = asyncServiceRequest;
    this.serviceLimitMap = serviceLimitMap ?? new Map();

    // Set all properties
    this.receivedAtE164Phone = this.callLog.getReceivedAtE164Phone();
    this.communicationTypeDetail = this.getPrettyEventType(callListMap, ivrsMap);
    this.day = this.callLog.getIncomingCallReceivedTime().tz(timezone).format('l');
    this.receiveTime = displayTime(this.callLog.getIncomingCallReceivedTime(), timezone, { timeOnly: true });
    this.holdDuration = formatDuration(this.callLog.getHoldDurationMS());
    this.end = displayTime(this.callLog.getServiceDeliveryEndTime(), timezone, { timeOnly: true });
    this.talkDuration = this.getTalkDurationString();
    this.answeredBy = getUserDisplayNameByUserId(
      allUserDataMap,
      this.callLog.getAnsweredBy() ?? asyncServiceRequest?.getAssignedTo(),
    );
    this.id = this.callLog.getId();
    this.disconnectReason = this.getDisconnectReasonString();
    this.stoppedDialingReason = this.getStoppedDialingReasonString();
    this.summary = this.getSummaryString();
    this.numCounselorsOnCall =
      this.callLog.getState() !== CallState.inProgressServiceNotSelected ? this.getNumCounselors() : '';
    this.hasAnonymousCaller = this.callLog.getHasAnonymousCaller()?.toString() || '';
    this.language =
      callLog.getLanguage() === undefined
        ? ''
        : SupportedLanguages.getDefaults(callLog.getLanguage()!).getDisplayName();
    this.incomingCallSID = callLog.getIncomingCallSid();
    this.alertType = this.getCSSClass();
    this.timestamp = callLog.getIncomingCallReceivedTime().toDate();
    this.hasCallbackRequest = this.getHasCallbackRequestString();
    this.forwardedTo = callLog.getForwardedTo();
    this.menuOption = this.getIvrOptionSelectedString();
  }

  private getHasCallbackRequestString(): string {
    const hasAsyncServiceRequestId = !isNil(this.callLog.getAsyncServiceRequestId());

    return hasAsyncServiceRequestId.toString();
  }

  private getIvrOptionSelectedString(): string | undefined {
    const responses = this.callLog.getResponses();
    const optionNames: string[] = [];

    for (const response of responses) {
      const name = response.getIvrOptionDisplayName();
      if (!isNil(name) && name.length > 0) {
        optionNames.push(name);
      }
    }

    return optionNames.length > 0 ? optionNames.join(' > ') : undefined;
  }

  /////////////////////////////////////////////////////////////////////////////
  // Pretty Event Type
  /////////////////////////////////////////////////////////////////////////////

  private hasEnforcedServiceLimit(): boolean {
    for (const execution of this.callLog.getServiceLimitExecutions()?.getExecutions()?.values() ?? []) {
      if (execution?.getActionExecuted() === true) {
        return true;
      }
    }

    return false;
  }

  public getPrettyEventType = (callListMap: Map<string, CallList>, ivrsMap: Map<string, IVR>): string => {
    const callList = callListMap.get(this.callLog.getCallListId());

    if (callList !== undefined) {
      return callList.getDisplayName();
    }
    if (this.callLog.getCallListId() !== undefined) {
      return this.callLog.getCallListId();
    }

    if (this.callLog.getOperatorDurationMS() === 0) {
      return 'No Option Selected / Caller Disconnected Before Hearing Greeting';
    }

    if (this.hasEnforcedServiceLimit()) {
      return 'Enforced Service Limit';
    }

    // Combine options selected into an 'event type'
    const responses = this.callLog.getResponses();
    const lastResponse = responses[responses.length - 1];
    const lastIVR = ivrsMap.get(lastResponse?.getIvrId());
    const optionPresses: string[] = [];
    for (const response of this.callLog.getResponses()) {
      const speech = response.getSpeech();
      const digits = response.getDigits();
      if (speech !== undefined && speech.length > 0) {
        optionPresses.push(speech);
      }
      if (digits !== undefined) {
        optionPresses.push(digits);
      }
    }
    if (optionPresses.length !== 0) {
      if (!isNil(this.callLog.getForwardedTo())) {
        return `Forwarded To / ${this.callLog.getForwardedTo()} / Selected: ${optionPresses.join(', ')}`;
      }
      const head = lastIVR?.getDisplayName() === undefined ? '' : `${lastIVR?.getDisplayName()} /`;
      let tail = 'Did Not Complete Selection';
      if (!isNil(this.callLog.getVoicemailMetadataId())) {
        tail = 'Left Voicemail';
      }
      if (!isNil(this.callLog.getAsyncServiceRequestId())) {
        tail = 'Requested Callback';
      }
      return `${head} Selected: ${optionPresses.join(', ')} / ${tail}`.trim();
    }

    if (!isNil(this.callLog.getForwardedTo())) {
      return `Forwarded To / ${this.callLog.getForwardedTo()}`;
    }

    if (this.callLog.getOperatorDurationMS() !== undefined) {
      let listenDuration = (this.callLog.getOperatorDurationMS()! - this.callLog.getRingDurationMS()) / 1000.0;
      if (listenDuration < 0) {
        listenDuration = 0;
      }

      return `No Option Selected / Listened For ${listenDuration}s`;
    }

    return 'No Option Selected';
  };

  /////////////////////////////////////////////////////////////////////////////
  // Pretty Print: Talk / Hold duration
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Return 'in progress' if the call is in progress. Else, return a string giving
   * the duration of the call in hours / minutes / seconds.
   */
  public getTalkDurationString = (): string => {
    if (this.callLog.getState() === CallState.inProgressServiceNotSelected) {
      return 'Waiting Client Selection';
    }

    if (this.callLog.getState() !== CallState.completed) {
      return 'In Progress';
    }

    const forwardedTalkDurationMS = this.getForwardedTalkDurationMS();
    if (!isNil(forwardedTalkDurationMS)) {
      return formatDuration(forwardedTalkDurationMS);
    }

    return formatDuration(this.callLog.getTalkDurationMS());
  };

  private getForwardedTalkDurationMS(): number | undefined {
    if (isEmpty(this.callLog.getForwardedTo())) {
      return undefined;
    }

    const responses = this.callLog.getResponses();
    const serviceDeliveryEndTime = this.callLog.getServiceDeliveryEndTime();
    if (responses.length === 0 || isNil(serviceDeliveryEndTime)) {
      return undefined;
    }

    const lastSelected = moment.max(responses.map((response) => response.getTimestamp()));
    if (isNil(lastSelected) || !lastSelected.isValid()) {
      return undefined;
    }

    return Math.max(serviceDeliveryEndTime.diff(lastSelected), 0);
  }

  private isSuspectedAbandonedForwardedCall(): boolean {
    const forwardedTalkDurationMS = this.getForwardedTalkDurationMS();

    return (
      !isNil(forwardedTalkDurationMS) && forwardedTalkDurationMS <= SUSPECTED_ABANDONED_FORWARDED_CALL_THRESHOLD_MS
    );
  }

  private getSuspectedAbandonedForwardedCallReason(): string {
    const forwardedToNumber = this.callLog.getForwardedTo() ?? 'unknown';

    return (
      'Suspected Abandoned Call: This forwarded call was unusually short. ' +
      `Review ${forwardedToNumber} for issues around routing, answering service, or phone menu complexity. ` +
      `If this issue recurs, consider auditing ${forwardedToNumber}: ` +
      'Here is our guide on how to audit your line: https://www.helplinesoftware.com/how-to-audit-your-hotline'
    );
  }

  private isSilentNoSelectionHangup(): boolean {
    return (
      !this.callLog.callListSelected() &&
      (this.callLog.getResponses() ?? []).length === 0 &&
      isEmpty(this.callLog.getForwardedTo()) &&
      !this.hasEnforcedServiceLimit() &&
      !this.callLog.wasClientConnectedToUser()
    );
  }

  /////////////////////////////////////////////////////////////////////////////
  // Summary
  /////////////////////////////////////////////////////////////////////////////

  public getSummaryString = (): string => {
    if (this.callLog.wasClientConnectedToUser()) {
      return this.getConnectedSummary();
    }

    return this.getNotConnectedSummary();
  };

  private getConnectedSummary(): string {
    const connected = `The caller was connected to ${this.answeredBy}.`;

    let disconnectedByLabel: string | undefined;
    switch (this.callLog.getDisconnectedBy()) {
      case DisconnectedBy.calleeValue: {
        disconnectedByLabel = this.answeredBy;
        break;
      }
      case DisconnectedBy.callerValue: {
        disconnectedByLabel = 'the caller';
        break;
      }
      case DisconnectedBy.server: {
        disconnectedByLabel = 'the server';
        break;
      }
      default: {
        if (this.callLog.isInProgress()) {
          return `${connected} It is still in progress.`;
        }

        return connected;
      }
    }

    return `${connected} They spoke for ${this.talkDuration} and the call was disconnected by ${disconnectedByLabel}.`;
  }

  private getNotConnectedSummary(): string {
    const sentences: string[] = [];

    const exceededServiceLimitName = this.getExceededServiceLimitName();
    const forwardedTo = this.callLog.getForwardedTo();

    if (!isNil(exceededServiceLimitName)) {
      sentences.push(
        `This was a call from ${exceededServiceLimitName} and not connected because service limit exceeded.`,
      );
    } else if (this.callLog.wasBlocked()) {
      sentences.push('The caller was not connected because blocked caller.');
    } else {
      if (isEmpty(forwardedTo)) {
        sentences.push(...this.getStoppedDialingSummarySentences());
      } else {
        sentences.push(
          `This call was unconditionally forwarded to ${forwardedTo}, and they spoke to that number for ${this.talkDuration}.`,
        );
      }

      if (!isNil(this.callLog.getVoicemailMetadataId())) {
        sentences.push('The caller left a voicemail.');
      }
    }

    const callbackSummary = this.getCallbackSummarySentence();
    if (!isNil(callbackSummary)) {
      sentences.push(callbackSummary);
    }

    return sentences.join(' ');
  }

  private getStoppedDialingSummarySentences(): string[] {
    switch (this.callLog.getStoppedDialingReason()) {
      case StoppedDialingReason.noUsersLeftToDial: {
        const numCounselorsOnCall = this.getNumCounselors();
        const updateSchedulesHelp = `updating the schedules specified here: ${CHANGE_WHO_IS_ON_CALL_HELP_URL}`;
        const addAdvocatesHelp = `And adding more people to the on-call schedule(s) by following these instructions: ${CHANGE_ADVOCATES_ASSIGNED_TO_SHIFT_HELP_URL}`;

        if (numCounselorsOnCall === '0') {
          return [
            'The call was disconnected because the on-call schedules listed for this call type were empty.',
            `You can add more people on call, you can do so by ${updateSchedulesHelp}`,
            addAdvocatesHelp,
          ];
        }

        return [
          'The call was disconnected because no on-call user was able to accept the call.',
          `There were ${numCounselorsOnCall} users on call.`,
          `If you want to add more people on call, you can do so by ${updateSchedulesHelp}`,
          addAdvocatesHelp,
        ];
      }
      case StoppedDialingReason.noCapacity: {
        return [
          `This call was not accepted because you have chosen to limit the maximum number of in-progress calls of this type to ${this.callList?.getCapacity()}. ` +
            'Please have your authorized representative reach out to support to change this if it is not expected.',
        ];
      }
      case StoppedDialingReason.incomingCallDisconnected: {
        return [
          'The caller changed their mind and hung up while we were following your instructions and dialing your on-call. ' +
            `They waited on hold for ${this.holdDuration}.`,
        ];
      }
      default: {
        return [];
      }
    }
  }

  private getCallbackSummarySentence(): string | undefined {
    if (isNil(this.callLog.getAsyncServiceRequestId())) {
      return undefined;
    }

    if (isNil(this.asyncServiceRequest?.getAssignedTo())) {
      return 'The caller requested a callback, which is still pending.';
    }

    return `The caller requested a callback, which was returned by ${this.answeredBy}.`;
  }

  private getExceededServiceLimitName(): string | undefined {
    const executions = this.callLog.getServiceLimitExecutions()?.getExecutions();
    if (isNil(executions)) {
      return undefined;
    }

    for (const [serviceLimitId, execution] of executions) {
      if (execution?.getExceeded() === true) {
        return this.serviceLimitMap.get(serviceLimitId)?.getDisplayName() ?? serviceLimitId;
      }
    }

    return undefined;
  }

  /////////////////////////////////////////////////////////////////////////////
  // Disconnect Reason
  /////////////////////////////////////////////////////////////////////////////

  public getDisconnectReasonString = (): string => {
    if (this.callLog.wasClientConnectedToUser()) {
      const disconnectedBy = this.callLog.getDisconnectedBy();

      if (disconnectedBy === DisconnectedBy.calleeValue) {
        return `Disconnected by ${this.answeredBy}`;
      }

      if (disconnectedBy === DisconnectedBy.callerValue) {
        return 'Disconnected by Caller';
      }

      return '';
    }

    if (!this.callLog.hadCapacity()) {
      return 'No Capacity';
    }

    if (this.isSilentNoSelectionHangup()) {
      return 'Legitimate Caller Changed Their Mind or Spam / Robocaller';
    }

    if (this.callLog.didClientDisconnectBeforeServiceDeliveryAttemptComplete()) {
      return 'Caller Disconnected Early';
    }

    if (this.callLog.wasBlocked()) {
      return 'Blocked';
    }

    if (this.isSuspectedAbandonedForwardedCall()) {
      return this.getSuspectedAbandonedForwardedCallReason();
    }

    if (this.callLog.getVoicemailMetadataId() !== undefined) {
      return 'Voicemail';
    }

    if (this.callLog.wasUnexpectedlyMissed()) {
      return 'No Answer';
    }

    return '';
  };

  /////////////////////////////////////////////////////////////////////////////
  // Should flag call for counselor
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Return true if we should flag this call for counselor attention. Else false.
   * In particular, this returns true if there is a critical system error.
   */
  public shouldFlagCallForCounselorAttention = (): boolean => {
    if (this.hasServerError()) {
      return true;
    }

    // Don't flag a call if no option was selected. Such calls always have 0 counselors listed.
    if (!this.callLog.callListSelected()) {
      return false;
    }

    /**
     * Flag if call ws disconnected and it's possible we have an organization issue. Eg,
     * too few counselors on staff, or counselors didn't answer.
     */
    if (!this.callLog.wasAnswered()) {
      /**
       * Step 1: In this case, the call may have been disconnected due to some
       * avoidable reason.
       */
      const avoidableDisconnectReasons = [DisconnectReason.error, DisconnectReason.noUsersLeftToDial];

      if (avoidableDisconnectReasons.includes(this.callLog.getDisconnectReason())) {
        return true;
      }

      /**
       * Step 2: Flag if we stopped dialing for an avoidable reason.
       */
      const avoidableStoppedDialingReasons = [StoppedDialingReason.noUsersLeftToDial];

      if (avoidableStoppedDialingReasons.includes(this.callLog.getStoppedDialingReason())) {
        return true;
      }
    }

    /**
     * If there were no users on call and call has no communication workflow then we should always flag, even if
     * client disconnected before we could start dialing.
     */
    if (this.callLog.getUserIdsToCall().length === 0 && !this.callLog.hasCommunicationWorkflow()) {
      return true;
    }

    return false;
  };

  /////////////////////////////////////////////////////////////////////////////
  // Should flag call for server error
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Return true if this call resulted in a server error.
   */
  public hasServerError = (): boolean => {
    if (this.callLog.getDisconnectReason() === DisconnectReason.error) {
      return true;
    }

    if (this.callLog.getHasCriticalServerError()) {
      return true;
    }

    return false;
  };

  /////////////////////////////////////////////////////////////////////////////
  // Stopped Dialing Reason
  /////////////////////////////////////////////////////////////////////////////

  public getStoppedDialingReasonString = (): string => {
    if (this.callLog.wasAnswered()) {
      return '';
    }

    const reason = this.callLog.getStoppedDialingReason();
    switch (reason) {
      case StoppedDialingReason.noUsersLeftToDial: {
        return 'No users left to dial';
      }
      case StoppedDialingReason.incomingCallDisconnected: {
        return 'Caller Disconnected';
      }
      case StoppedDialingReason.wasAnswered: {
        return 'Was Answered';
      }
      case StoppedDialingReason.noCapacity: {
        return 'No Capacity';
      }
      case undefined: {
        return '';
      }
      default: {
        console.error(`getStoppedDialingReasonString: Unknown reason='${reason}'`);
        return '';
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////
  // Number of Counselors
  /////////////////////////////////////////////////////////////////////////////

  public getNumCounselors = (): string => uniq(this.callLog.getUserIdsToCall()).length.toString();

  /////////////////////////////////////////////////////////////////////////////
  // CSS
  /////////////////////////////////////////////////////////////////////////////

  public getCSSClass = (): CommunicationDisplayAlertType => {
    if (this.callLog.didSendDigits()) {
      return CommunicationDisplayAlertType.sentDigits;
    }

    if (this.hasServerError()) {
      return CommunicationDisplayAlertType.flagServer;
    }

    if (this.callLog.getBlockedCallerId() !== undefined) {
      return CommunicationDisplayAlertType.blocked;
    }

    if (this.callLog.getState() === CallState.inProgressServiceNotSelected) {
      return CommunicationDisplayAlertType.null;
    }

    if (this.callLog.isInProgress()) {
      return CommunicationDisplayAlertType.inProgress;
    }

    if (this.isSuspectedAbandonedForwardedCall()) {
      return CommunicationDisplayAlertType.unanswered;
    }

    if (this.shouldFlagCallForCounselorAttention()) {
      return CommunicationDisplayAlertType.unanswered;
    }

    // No special CSS
    return CommunicationDisplayAlertType.null;
  };
}
